import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, FileText, HardHat, Clock, AlertTriangle, CheckCircle2, Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const EUR = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function PublicInspector() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/inspector/${token}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Token inválido o expirado");
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-10 w-10 animate-spin text-[#6D43B8]" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="rounded-2xl p-8 bg-white shadow-xl text-center max-w-md">
        <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-[#B43A4E]" />
        <h2 className="text-xl font-bold mb-1">No verificable</h2>
        <p className="text-sm text-slate-600">{error}</p>
      </div>
    </div>
  );

  const p = data.proyecto;
  const cls = p.clasificacion || {};
  const tipoColor = cls.tipo_documento === "ESS" ? "#B43A4E" : cls.tipo_documento === "PSS" ? "#1E5BCC" : "#0F8A5F";

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Cabecera verificación */}
        <div className="rounded-2xl p-6 bg-white shadow-md text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto text-[#0F8A5F] mb-2" />
          <h1 className="text-xl font-bold">Documento verificado</h1>
          <p className="text-xs text-slate-500 mt-1">LEGATUM CAPITAL — verificación pública sin login</p>
          <p className="text-[10px] text-slate-400 mt-2">Consultado: {new Date(data.verificado_at).toLocaleString("es-ES")}</p>
        </div>

        {/* Tarjeta del PSS */}
        <div className="rounded-2xl bg-white shadow-md overflow-hidden">
          <div className="px-6 py-4 text-white" style={{ background: `linear-gradient(135deg, ${tipoColor}, ${tipoColor}cc)` }}>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center text-2xl font-bold">
                {cls.tipo_documento}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest opacity-80">{cls.tipo_documento === "PSS" ? "Plan" : cls.tipo_documento === "ESS" ? "Estudio" : "Estudio Básico"} de Seguridad y Salud</div>
                <div className="text-lg font-bold truncate">{p.nombre}</div>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-2">
            <Field label="Emplazamiento" v={[p.emplazamiento, p.municipio, p.provincia].filter(Boolean).join(" · ") || "—"} />
            <Field label="PEC" v={EUR(p.presupuesto_pec)} />
            <Field label="Plazo" v={`${p.plazo_dias_laborables || 0} días laborables`} />
            <Field label="Trabajadores máx." v={p.max_trabajadores_simultaneos || 0} />
            {p.obra_nombre && <Field label="Obra" v={p.obra_nombre} icon={HardHat} />}
            {p.licitacion_expediente && <Field label="Expediente" v={p.licitacion_expediente} />}

            <div className="border-t pt-3 mt-3">
              <div className="flex items-center gap-2 text-sm">
                <ShieldCheck className="h-5 w-5 text-[#0F8A5F]" />
                <div>
                  <div className="font-bold text-[#0F8A5F]">Firmado PAdES</div>
                  <div className="text-xs text-slate-500">Por <b>{p.firma_signer || "—"}</b> · {new Date(p.firma_fecha).toLocaleString("es-ES")}</div>
                </div>
              </div>
            </div>

            <Button
              onClick={() => window.open(`${API}/public/inspector/${token}/pdf`, "_blank")}
              className="w-full bg-[#1E5BCC] text-white mt-3"
              data-testid="inspector-pdf"
            >
              <FileText className="h-4 w-4 mr-1" />Ver PDF firmado
            </Button>
          </div>
        </div>

        {/* Trabajadores hoy */}
        <div className="rounded-2xl bg-white shadow-md p-5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#0F8A5F] mb-2 flex items-center gap-1">
            <Clock className="h-3 w-3" />Trabajadores fichados hoy
          </div>
          {data.trabajadores_fichados_hoy.length === 0 ? (
            <div className="text-sm text-slate-500">Sin fichajes hoy o Control Horario no activo para esta obra.</div>
          ) : (
            <div className="space-y-1">
              {data.trabajadores_fichados_hoy.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-dashed last:border-0">
                  <span>{t.nombre}</span>
                  <Badge className="text-[10px] bg-[#0F8A5F]/15 text-[#0F8A5F]">{t.estado || "activo"}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bitácora reciente */}
        <div className="rounded-2xl bg-white shadow-md p-5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#127372] mb-2 flex items-center gap-1">
            <Activity className="h-3 w-3" />Bitácora reciente
          </div>
          {data.eventos_recientes.length === 0 ? (
            <div className="text-sm text-slate-500">Sin eventos.</div>
          ) : (
            <ol className="space-y-1.5">
              {data.eventos_recientes.map((ev, i) => (
                <li key={i} className="text-xs flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#127372] mt-1.5 shrink-0" />
                  <div>
                    <div className="font-bold capitalize">{(ev.evento || "").replace(/_/g, " ")}</div>
                    <div className="text-slate-500">{new Date(ev.timestamp).toLocaleString("es-ES")} · {ev.user_name || "sistema"}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="text-center text-[10px] text-slate-400">
          Powered by LEGATUM CAPITAL · Verificación PSS conforme al RD 1627/1997
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, icon: Ic }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <div className="text-slate-500 flex items-center gap-1">{Ic && <Ic className="h-3 w-3" />}{label}</div>
      <div className="font-bold text-right truncate max-w-[60%]">{v}</div>
    </div>
  );
}
